import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { PartnerCloudflareIntegration } from '@jane/business-admin/types';
import { janeApiV2 } from '@jane/shared/data-access';

const cloudflareIntegrationsUrl = (menuId: number) =>
  `/managers/bloom_menus/${menuId}/cloudflare_integrations`;

const cloudflareIntegrationUrl = (menuId: number, integrationId: number) =>
  `/managers/bloom_menus/${menuId}/cloudflare_integrations/${integrationId}`;

const fetchCloudflareIntegrations = async (
  menuId: number
): Promise<PartnerCloudflareIntegration[]> => {
  const data = await janeApiV2.get<{
    partner_cloudflare_integrations: PartnerCloudflareIntegration[];
  }>(cloudflareIntegrationsUrl(menuId));

  return data['partner_cloudflare_integrations'];
};

export const useFetchCloudflareIntegrations = (menuId?: number) => {
  return useQuery({
    queryFn: () => fetchCloudflareIntegrations(menuId!),
    queryKey: ['partner_cloudflare_integrations', menuId],
    enabled: !!menuId,
  });
};

type CreateCloudflareIntegrationParams = {
  account_id: string;
  api_token: string;
  deploy_robots_txt?: boolean;
  name?: string;
  route_pattern?: string;
  zone_id: string;
};

export const useCreateCloudflareIntegration = (menuId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: CreateCloudflareIntegrationParams) => {
      if (!menuId) return null;
      return await janeApiV2.post(cloudflareIntegrationsUrl(menuId), args);
    },
    mutationKey: ['create-cloudflare-integration', menuId],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['partner_cloudflare_integrations', menuId],
      });
    },
  });
};

type UpdateCloudflareIntegrationParams = {
  account_id?: string;
  api_token?: string;
  deploy_robots_txt?: boolean;
  name?: string;
  route_pattern?: string;
  zone_id?: string;
};

export const useUpdateCloudflareIntegration = (
  menuId?: number,
  integrationId?: number
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: UpdateCloudflareIntegrationParams) => {
      if (!menuId || !integrationId) return null;
      return await janeApiV2.patch(
        cloudflareIntegrationUrl(menuId, integrationId),
        args
      );
    },
    mutationKey: ['update-cloudflare-integration', menuId, integrationId],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['partner_cloudflare_integrations', menuId],
      });
    },
  });
};

export const useDeployCloudflareIntegration = (
  menuId?: number,
  integrationId?: number
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!menuId || !integrationId) return null;
      return await janeApiV2.post(
        `${cloudflareIntegrationUrl(menuId, integrationId)}/provision`,
        {}
      );
    },
    mutationKey: ['deploy-cloudflare-integration', menuId, integrationId],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['partner_cloudflare_integrations', menuId],
      });
    },
  });
};

export const useDeleteCloudflareIntegration = (
  menuId?: number,
  integrationId?: number
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!menuId || !integrationId) return null;
      return await janeApiV2.delete(
        cloudflareIntegrationUrl(menuId, integrationId)
      );
    },
    mutationKey: ['delete-cloudflare-integration', menuId, integrationId],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['partner_cloudflare_integrations', menuId],
      });
    },
  });
};

export const useDeactivateCloudflareIntegration = (
  menuId?: number,
  integrationId?: number
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!menuId || !integrationId) return null;
      return await janeApiV2.post(
        `${cloudflareIntegrationUrl(menuId, integrationId)}/deprovision`,
        {}
      );
    },
    mutationKey: ['deactivate-cloudflare-integration', menuId, integrationId],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['partner_cloudflare_integrations', menuId],
      });
    },
  });
};
